import React, { useEffect, useRef, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Copy, Loader2, AlertCircle, Search } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import IntellendLogo from "@/components/IntellendLogo";

// ─── Turnstile type augmentation ────────────────────────────────────────────
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact";
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

// ─── Zod schemas ─────────────────────────────────────────────────────────────
const step1Schema = z.object({
  name: z
    .string()
    .min(2, "Full name must be at least 2 characters")
    .max(200, "Full name must not exceed 200 characters"),
  phone: z
    .string()
    .regex(/^\d{10}$/, "Enter a valid 10-digit mobile number"),
  grievance_text: z
    .string()
    .min(20, "Please describe your grievance in at least 20 characters")
    .max(2000, "Grievance description must not exceed 2000 characters"),
});

type Step1Data = z.infer<typeof step1Schema>;

const lookupSchema = z.object({
  utr: z
    .string()
    .regex(/^GRV-\d{8}-[A-Z0-9]{6}$/, "Enter a valid UTR (e.g. GRV-20260625-AB3X9Z)"),
});

type LookupData = z.infer<typeof lookupSchema>;

// ─── Turnstile hook ───────────────────────────────────────────────────────────
// NOTE: this MUST be the *site* key (public, safe to ship in the bundle), not the
// secret key. It was previously named VITE_TURNSTILE_SECRET_KEY, which no .env
// file ever defined — so every build silently fell back to the Cloudflare test
// key below and the widget rendered the red "For testing only" banner in
// production with zero bot protection. See CLOUDFLARE_TURNSTILE_UPDATE.md.
const TURNSTILE_SITE_KEY =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) ||
  "1x00000000000000000000AA"; // Cloudflare test key (always passes) — local dev only

// Module-level registry so multiple useTurnstile instances don't clobber each other's onload callback
const _pendingRenders = new Set<() => void>();

function _ensureTurnstileScript() {
  if (document.getElementById("cf-turnstile-script")) return;
  window.onTurnstileLoad = () => {
    _pendingRenders.forEach((fn) => fn());
    _pendingRenders.clear();
  };
  const script = document.createElement("script");
  script.id = "cf-turnstile-script";
  script.src =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit";
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

function useTurnstile(containerId: string, enabled = true) {
  const [token, setToken] = useState<string | null>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);

  const renderWidget = useCallback(() => {
    if (!window.turnstile) return;
    const container = document.getElementById(containerId);
    if (!container) return;
    container.innerHTML = "";
    widgetIdRef.current = window.turnstile.render(container, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (t) => setToken(t),
      "expired-callback": () => setToken(null),
      "error-callback": () => setToken(null),
      theme: "light",
    });
  }, [containerId]);

  useEffect(() => {
    if (!enabled) return;
    if (window.turnstile) {
      renderWidget();
      return;
    }
    _pendingRenders.add(renderWidget);
    _ensureTurnstileScript();
    return () => { _pendingRenders.delete(renderWidget); };
  }, [renderWidget, enabled]);

  const reset = useCallback(() => {
    setToken(null);
    if (window.turnstile && widgetIdRef.current !== undefined) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  return { token, reset };
}

// ─── Main component ───────────────────────────────────────────────────────────
type Step = "form" | "otp" | "success";

interface SubmissionResult {
  utr: string;
  submitted_at: string;
}

interface LookupResult {
  utr: string;
  name: string;
  status: string;
  status_display: string;
  submitted_at: string;
  updated_at: string;
}

const Grievance: React.FC = () => {
  const { toast } = useToast();

  // ── Step state ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>("form");
  const [submittedData, setSubmittedData] = useState<Step1Data | null>(null);
  const [result, setResult] = useState<SubmissionResult | null>(null);

  // ── OTP state ───────────────────────────────────────────────────────────────
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // ── Form loading ────────────────────────────────────────────────────────────
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ── UTR lookup ──────────────────────────────────────────────────────────────
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // ── Turnstile ───────────────────────────────────────────────────────────────
  const { token: turnstileToken, reset: resetTurnstile } = useTurnstile(
    "grievance-turnstile-widget"
  );
  const { token: lookupTurnstileToken, reset: resetLookupTurnstile } = useTurnstile(
    "lookup-turnstile-widget"
  );

  // ── Step 1 form ─────────────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<Step1Data>({ resolver: zodResolver(step1Schema) });

  // ── Lookup form ─────────────────────────────────────────────────────────────
  const {
    register: registerLookup,
    handleSubmit: handleLookupSubmit,
    formState: { errors: lookupErrors },
  } = useForm<LookupData>({ resolver: zodResolver(lookupSchema) });

  // ── Resend cooldown timer ───────────────────────────────────────────────────
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // ── Step 1 submit: send OTP ─────────────────────────────────────────────────
  const onStep1Submit = async (data: Step1Data) => {
    if (!turnstileToken) {
      setFormError("Please complete the CAPTCHA challenge.");
      return;
    }
    setFormError(null);
    setFormLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/grievance/send_otp/`, {
        phone: data.phone,
        turnstile_token: turnstileToken,
      });
      setSubmittedData(data);
      setStep("otp");
      setResendCooldown(30);
      toast({ title: "OTP Sent", description: `OTP sent to ******${data.phone.slice(-4)}` });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        "Failed to send OTP. Please try again.";
      setFormError(msg);
      resetTurnstile();
    } finally {
      setFormLoading(false);
    }
  };

  // ── Resend OTP ──────────────────────────────────────────────────────────────
  const handleResendOtp = async () => {
    if (!submittedData || resendCooldown > 0) return;
    setOtpError(null);
    setResendCooldown(30);
    try {
      // Re-use the last Turnstile token for resend (or prompt user to refresh)
      await axios.post(`${API_BASE_URL}/api/grievance/send_otp/`, {
        phone: submittedData.phone,
        turnstile_token: turnstileToken || "resend",
      });
      toast({ title: "OTP Resent", description: "A new OTP has been sent." });
    } catch {
      setOtpError("Failed to resend OTP. Please go back and try again.");
    }
  };

  // ── Step 2 submit: verify OTP + save grievance ──────────────────────────────
  const onOtpSubmit = async () => {
    if (otp.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP.");
      return;
    }
    if (!submittedData) return;

    setOtpError(null);
    setOtpLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/grievance/submit/`, {
        name: submittedData.name,
        phone: submittedData.phone,
        grievance_text: submittedData.grievance_text,
        otp,
        turnstile_token: turnstileToken || "",
      });
      setResult(response.data);
      setStep("success");
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { error?: string; errors?: Record<string, string> } } })
        ?.response?.data;
      if (errData?.errors) {
        setOtpError(Object.values(errData.errors).join(" "));
      } else {
        setOtpError(errData?.error || "Submission failed. Please try again.");
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // ── UTR lookup ──────────────────────────────────────────────────────────────
  const onLookupSubmit = async (data: LookupData) => {
    if (!lookupTurnstileToken) {
      setLookupError("Please complete the CAPTCHA challenge.");
      return;
    }
    setLookupError(null);
    setLookupResult(null);
    setLookupLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/grievance/lookup/`, {
        params: { utr: data.utr.toUpperCase() },
      });
      setLookupResult(response.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        "Grievance not found.";
      setLookupError(msg);
    } finally {
      setLookupLoading(false);
      resetLookupTurnstile();
    }
  };

  // ── Copy UTR ────────────────────────────────────────────────────────────────
  const copyUtr = () => {
    if (!result?.utr) return;
    navigator.clipboard.writeText(result.utr).then(() => {
      toast({ title: "Copied!", description: "UTR copied to clipboard." });
    });
  };

  // ── Status badge colour ─────────────────────────────────────────────────────
  const statusColour = (s: string) => {
    switch (s) {
      case "RECEIVED":
        return "bg-blue-100 text-blue-800";
      case "IN_REVIEW":
        return "bg-yellow-100 text-yellow-800";
      case "RESOLVED":
        return "bg-green-100 text-green-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <IntellendLogo size="sm" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Grievance Portal</h1>
            <p className="text-xs text-gray-500">Submit and track your grievances</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {/* ── Step 1: Form ─────────────────────────────────────────────────── */}
        {step === "form" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Submit a Grievance</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Fill in your details below. Your phone number will be verified via OTP.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onStep1Submit)} className="space-y-5">
                {/* Name */}
                <div className="space-y-1">
                  <Label htmlFor="name">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="Enter your full name"
                    {...register("name")}
                    className={errors.name ? "border-red-400" : ""}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500">{errors.name.message}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="space-y-1">
                  <Label htmlFor="phone">
                    Mobile Number <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
                      +91
                    </span>
                    <Input
                      id="phone"
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      className={`rounded-l-none ${errors.phone ? "border-red-400" : ""}`}
                      {...register("phone")}
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-xs text-red-500">{errors.phone.message}</p>
                  )}
                </div>

                {/* Grievance text */}
                <div className="space-y-1">
                  <Label htmlFor="grievance_text">
                    Grievance Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="grievance_text"
                    placeholder="Please describe your grievance in detail (minimum 20 characters)"
                    rows={5}
                    className={errors.grievance_text ? "border-red-400" : ""}
                    {...register("grievance_text")}
                  />
                  {errors.grievance_text && (
                    <p className="text-xs text-red-500">{errors.grievance_text.message}</p>
                  )}
                </div>

                {/* Cloudflare Turnstile */}
                <div className="space-y-1">
                  <Label>Security Verification <span className="text-red-500">*</span></Label>
                  <div id="grievance-turnstile-widget" className="mt-1" />
                  {!turnstileToken && formLoading && (
                    <p className="text-xs text-amber-600">Please complete the CAPTCHA above.</p>
                  )}
                </div>

                {/* Error */}
                {formError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={formLoading || !turnstileToken}
                >
                  {formLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending OTP…
                    </>
                  ) : (
                    "Send OTP & Continue"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ── Step 2: OTP Verification ──────────────────────────────────────── */}
        {step === "otp" && submittedData && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Verify Your Phone</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Enter the 6-digit OTP sent to{" "}
                <span className="font-medium">
                  +91 ******{submittedData.phone.slice(-4)}
                </span>
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* OTP input */}
              <div className="flex flex-col items-center gap-4">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={(val) => {
                    setOtp(val);
                    setOtpError(null);
                  }}
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>

                {otpError && (
                  <Alert variant="destructive" className="w-full">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{otpError}</AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Submit OTP */}
              <Button
                className="w-full"
                onClick={onOtpSubmit}
                disabled={otpLoading || otp.length !== 6}
              >
                {otpLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  "Verify & Submit Grievance"
                )}
              </Button>

              {/* Resend + Back */}
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  className="text-gray-500 hover:underline"
                  onClick={() => {
                    setStep("form");
                    setOtp("");
                    setOtpError(null);
                  }}
                >
                  ← Go back
                </button>
                <button
                  type="button"
                  className={`font-medium ${
                    resendCooldown > 0
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 hover:underline"
                  }`}
                  disabled={resendCooldown > 0}
                  onClick={handleResendOtp}
                >
                  {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : "Resend OTP"}
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Step 3: Success ───────────────────────────────────────────────── */}
        {step === "success" && result && (
          <Card className="border-green-200 bg-green-50">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-6 w-6" />
                <CardTitle className="text-xl text-green-800">
                  Grievance Submitted Successfully!
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Your grievance has been recorded. Please save your Unique Tracking
                Reference (UTR) to check the status later.
              </p>

              {/* UTR display */}
              <div className="bg-white rounded-lg border border-green-200 p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Your UTR
                  </p>
                  <p className="text-2xl font-mono font-bold text-gray-900">
                    {result.utr}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={copyUtr}>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
              </div>

              <p className="text-xs text-gray-500">
                Submitted on{" "}
                {new Date(result.submitted_at).toLocaleString("en-IN", {
                  dateStyle: "long",
                  timeStyle: "short",
                })}
              </p>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setStep("form");
                  setOtp("");
                  setResult(null);
                  setSubmittedData(null);
                  resetTurnstile();
                }}
              >
                Submit Another Grievance
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── UTR Lookup section (always visible) ──────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="h-4 w-4" />
              Track Your Grievance
            </CardTitle>
            <p className="text-sm text-gray-500">
              Already have a UTR? Enter it below to check the status.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLookupSubmit(onLookupSubmit)} className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. GRV-20260625-AB3X9Z"
                  className={`font-mono uppercase ${lookupErrors.utr ? "border-red-400" : ""}`}
                  {...registerLookup("utr")}
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase();
                    registerLookup("utr").onChange(e);
                  }}
                />
                <Button type="submit" variant="outline" disabled={lookupLoading || !lookupTurnstileToken}>
                  {lookupLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Track"
                  )}
                </Button>
              </div>
              {lookupErrors.utr && (
                <p className="text-xs text-red-500">{lookupErrors.utr.message}</p>
              )}

              <div id="lookup-turnstile-widget" />

              {lookupError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{lookupError}</AlertDescription>
                </Alert>
              )}

              {lookupResult && (
                <div className="rounded-lg border bg-white p-4 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold text-gray-800">
                      {lookupResult.utr}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColour(
                        lookupResult.status
                      )}`}
                    >
                      {lookupResult.status_display}
                    </span>
                  </div>
                  <p className="text-gray-600">
                    <span className="font-medium">Name:</span> {lookupResult.name}
                  </p>
                  <p className="text-gray-500 text-xs">
                    Submitted:{" "}
                    {new Date(lookupResult.submitted_at).toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </p>
                  <p className="text-gray-500 text-xs">
                    Last updated:{" "}
                    {new Date(lookupResult.updated_at).toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </p>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 pb-4">
          For urgent matters, please contact our support team directly.
        </p>
      </main>
    </div>
  );
};

export default Grievance;
